import { db } from '../config/firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit
} from 'firebase/firestore';

/**
 * Busca todas as avaliações
 */
export const getAllEvaluations = async (maxResults = 1000) => {
  try {
    const evaluationsRef = collection(db, 'avaliacoes');
    const q = query(
      evaluationsRef,
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const querySnapshot = await getDocs(q);
    const evaluations = [];

    querySnapshot.forEach((doc) => {
      evaluations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return evaluations;
  } catch (error) {
    console.error('Erro ao buscar avaliações:', error);
    return [];
  }
};

/**
 * Busca avaliações por projeto
 */
export const getEvaluationsByCourse = async (courseName) => {
  try {
    const evaluationsRef = collection(db, 'avaliacoes');
    const q = query(
      evaluationsRef,
      where('courseName', '==', courseName),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const evaluations = [];

    querySnapshot.forEach((doc) => {
      evaluations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return evaluations;
  } catch (error) {
    // Handle the specific error for missing index
    if (error.code === 'failed-precondition' && error.message.includes('index')) {
      console.warn('Índice necessário não encontrado. Buscando avaliações de forma alternativa...');

      // Alternative approach: fetch all evaluations and filter client-side
      // This is less efficient but will work until the index is created
      try {
        const allEvaluations = await getAllEvaluations(1000); // Limit to avoid performance issues
        return allEvaluations.filter(evaluation =>
          evaluation.courseName === courseName
        ).sort((a, b) => {
          // Sort by createdAt in descending order (most recent first)
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
      } catch (fallbackError) {
        console.error('Erro ao buscar avaliações por projeto (modo alternativo):', fallbackError);
        return [];
      }
    } else {
      console.error('Erro ao buscar avaliações por projeto:', error);
      return [];
    }
  }
};

/**
 * Calcula estatísticas gerais
 */
export const getGeneralStats = async () => {
  try {
    const evaluations = await getAllEvaluations();

    if (evaluations.length === 0) {
      return {
        totalEvaluations: 0,
        averageRating: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        courseStats: {},
        userStats: {},
        recentEvaluations: []
      };
    }

    // Calcula média de avaliações
    const totalRating = evaluations.reduce((sum, evaluation) => sum + (evaluation.rating || 0), 0);
    const averageRating = (totalRating / evaluations.length).toFixed(2);

    // Distribuição de notas
    const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    evaluations.forEach(evaluation => {
      if (evaluation.rating >= 1 && evaluation.rating <= 5) {
        ratingDistribution[evaluation.rating]++;
      }
    });

    // Estatísticas por projeto
    const courseStats = {};
    evaluations.forEach(evaluation => {
      const courseName = evaluation.courseName || 'Sem projeto';
      if (!courseStats[courseName]) {
        courseStats[courseName] = {
          count: 0,
          totalRating: 0,
          ratings: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        };
      }
      courseStats[courseName].count++;
      courseStats[courseName].totalRating += evaluation.rating || 0;
      if (evaluation.rating >= 1 && evaluation.rating <= 5) {
        courseStats[courseName].ratings[evaluation.rating]++;
      }
    });

    // Calcula média por projeto
    Object.keys(courseStats).forEach(course => {
      const stats = courseStats[course];
      stats.average = (stats.totalRating / stats.count).toFixed(2);
    });

    // Estatísticas por usuário
    const userStats = {};
    evaluations.forEach(evaluation => {
      const userId = evaluation.userId || 'anonimo';
      if (!userStats[userId]) {
        userStats[userId] = { count: 0 };
      }
      userStats[userId].count++;
    });

    return {
      totalEvaluations: evaluations.length,
      averageRating,
      ratingDistribution,
      courseStats,
      userStats,
      recentEvaluations: evaluations.slice(0, 5)
    };
  } catch (error) {
    console.error('Erro ao calcular estatísticas:', error);
    return null;
  }
};

/**
 * Busca lista de projetos únicos
 */
export const getUniqueCourses = async () => {
  try {
    const evaluations = await getAllEvaluations();
    const courses = new Set();

    evaluations.forEach(evaluation => {
      if (evaluation.courseName) {
        courses.add(evaluation.courseName);
      }
    });

    return Array.from(courses).sort();
  } catch (error) {
    console.error('Erro ao buscar projetos:', error);
    return [];
  }
};
